import { createContext, useContext, useEffect, useState, useCallback } from "react";
import http, { authMe, authLogout, authSession, authGuest, setMyTimezone } from "./api";

const AuthContext = createContext({
  user: null,
  loading: true,
  login: () => {},
  logout: async () => {},
  continueAsGuest: async () => {},
  exchangeSessionId: async () => {},
});

// LocalStorage key used by AuthCallback (in a popup/new-tab) to hand the
// session_id back to the opener/parent window on browsers (iOS Safari!) where
// postMessage across the auth.emergentagent.com redirect chain is unreliable.
const SESSION_HANDOFF_KEY = "recce:auth:handoff";
const SESSION_HANDOFF_ERROR_KEY = "recce:auth:handoff:error";

export const detectPlatform = () => {
  if (typeof window === "undefined") return { isMobile: false, isIOS: false };
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua) || (/Mac/i.test(ua) && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  const isMobileUA = /Mobile|Tablet|CriOS|FxiOS/i.test(ua) || isIOS || isAndroid;
  const isSmallViewport = window.innerWidth < 820;
  const isTouchOnly = window.matchMedia?.("(pointer: coarse)").matches;
  const isMobile = isMobileUA || isSmallViewport || isTouchOnly;
  return { isMobile, isIOS, isAndroid };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const u = await authMe();
      setUser(u);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync the browser's local timezone to the server once per login — the
  // always-on scheduler needs it to pick 8pm / 2am / 6am local slots.
  useEffect(() => {
    if (!user?.user_id) return;
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      if (tz && tz !== user.timezone) {
        setMyTimezone(tz).catch(() => {});
      }
    } catch {
      /* ignore */
    }
  }, [user?.user_id]);

  useEffect(() => {
    // If we're returning from OAuth callback, AuthCallback will exchange the session_id
    // and set the cookie before we should run /me. Don't race it.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const exchangeSessionId = useCallback(async (sessionId) => {
    const resp = await authSession(sessionId);
    if (resp?.user) setUser(resp.user);
    return resp?.user;
  }, []);

  const login = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    const { isMobile } = detectPlatform();

    // Clear any stale handoff values before we start.
    try {
      localStorage.removeItem(SESSION_HANDOFF_KEY);
      localStorage.removeItem(SESSION_HANDOFF_ERROR_KEY);
    } catch {}

    // Dual channel: popup/new-tab + localStorage handoff.
    //  • Desktop → centred popup window.
    //  • Mobile  → new tab (iOS still allows this; it becomes a foreground tab).
    //    Even if iOS Safari ITP breaks postMessage, the AuthCallback writes the
    //    session_id to localStorage from the new tab, which the opener polls.
    const target = "recce-google-auth";
    const features = isMobile
      ? undefined
      : (() => {
          const w = Math.min(480, window.innerWidth - 40);
          const h = Math.min(640, window.innerHeight - 40);
          const left = window.screenX + (window.innerWidth - w) / 2;
          const top = window.screenY + (window.innerHeight - h) / 2;
          return `popup=yes,width=${w},height=${h},left=${left},top=${top},noopener=no`;
        })();
    const popup = window.open(authUrl, target, features);

    // If the browser (iOS Safari in Private Browsing, popup-blocker) refuses to
    // open the window at all, fall back to a same-tab navigation.
    if (!popup) {
      window.location.href = authUrl;
      return;
    }

    // 1) postMessage path (works on desktop; may be severed on iOS Safari).
    const onMessage = async (ev) => {
      if (!ev?.data || typeof ev.data !== "object") return;
      if (ev.data.type === "recce:auth:success" && ev.data.sessionId) {
        cleanup();
        try {
          await exchangeSessionId(ev.data.sessionId);
        } catch {}
      } else if (ev.data.type === "recce:auth:error") {
        cleanup();
      }
    };
    window.addEventListener("message", onMessage);

    // 2) localStorage handoff path (resilient on iOS). AuthCallback writes
    //    { sessionId, ts } to recce:auth:handoff; we pick it up here.
    const onStorage = async (ev) => {
      if (ev.key !== SESSION_HANDOFF_KEY || !ev.newValue) return;
      try {
        const payload = JSON.parse(ev.newValue);
        if (payload?.sessionId) {
          cleanup();
          try {
            await exchangeSessionId(payload.sessionId);
          } catch {}
          try { localStorage.removeItem(SESSION_HANDOFF_KEY); } catch {}
        }
      } catch {}
    };
    window.addEventListener("storage", onStorage);

    // 3) Poll fallback for same-origin tabs where `storage` events don't fire
    //    (same window writes don't fire storage events in some browsers).
    const poll = setInterval(async () => {
      try {
        const raw = localStorage.getItem(SESSION_HANDOFF_KEY);
        if (raw) {
          const payload = JSON.parse(raw);
          if (payload?.sessionId) {
            cleanup();
            try {
              await exchangeSessionId(payload.sessionId);
            } catch {}
            try { localStorage.removeItem(SESSION_HANDOFF_KEY); } catch {}
            return;
          }
        }
      } catch {}
      if (popup.closed) {
        cleanup();
      }
    }, 500);

    function cleanup() {
      clearInterval(poll);
      window.removeEventListener("message", onMessage);
      window.removeEventListener("storage", onStorage);
    }
  }, [exchangeSessionId]);

  const logout = async () => {
    try {
      await authLogout();
    } catch {
      // ignore
    }
    setUser(null);
    window.location.href = "/";
  };

  const continueAsGuest = async () => {
    const resp = await authGuest();
    if (resp?.user) setUser(resp.user);
    return resp?.user;
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, continueAsGuest, exchangeSessionId }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export const AUTH_HANDOFF_KEY = SESSION_HANDOFF_KEY;
export const AUTH_HANDOFF_ERROR_KEY = SESSION_HANDOFF_ERROR_KEY;

// Re-export the axios client for callers that want it directly
export { http };
